import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';

// Types
interface DataPoint {
  month: string;
  revenue: number;
  orders: number;
  avgTicket: number;
  adSpend: number;
  roi: number;
  cpa: number;
  growth: number;
  isProjected?: boolean;
}

interface MetricCardProps {
  title: string;
  current: number;
  previous: number;
  formatter?: (value: number) => string;
  isGood?: (current: number, previous: number) => boolean;
}

type PageType = 'home' | 'mobility' | 'campaign-cost';

// Home Page Component
const HomePage: React.FC<{ onNavigate: (page: PageType) => void }> = ({ onNavigate }) => {
  const dashboardOptions = [
    {
      id: 'mobility' as PageType,
      title: 'Mobility Dashboard',
      description: 'View performance analytics for Mobility Scooter Rental campaigns',
      icon: '📊',
      features: ['Revenue tracking', 'ROI analysis', '12-month projections', 'Growth metrics']
    },
    {
      id: 'campaign-cost' as PageType,
      title: 'Estimate Campaign Cost per Niche',
      description: 'Calculate and estimate advertising costs for different market niches',
      icon: '💰',
      features: ['Cost estimation', 'Niche analysis', 'Budget planning', 'ROI forecasting']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Digital Marketing Analytics Suite
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Comprehensive tools for tracking campaign performance, estimating costs, and optimizing your digital marketing strategies.
          </p>
        </div>

        {/* Dashboard Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {dashboardOptions.map((option) => (
            <div
              key={option.id}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
              onClick={() => onNavigate(option.id)}
            >
              <div className="p-8">
                <div className="text-center mb-6">
                  <div className="text-6xl mb-4">{option.icon}</div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {option.title}
                  </h2>
                  <p className="text-gray-600">
                    {option.description}
                  </p>
                </div>

                <div className="space-y-2 mb-6">
                  {option.features.map((feature, index) => (
                    <div key={index} className="flex items-center text-sm text-gray-600">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                      {feature}
                    </div>
                  ))}
                </div>

                <button className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                  Open Dashboard
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center mt-16 text-gray-500">
          <p>Built for optimizing Google Ads campaigns and digital marketing performance</p>
        </div>
      </div>
    </div>
  );
};

// Mobility Dashboard Component (moved from previous implementation)
const MobilityDashboard: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [showProjections, setShowProjections] = useState<boolean>(false);
  
  // Historical data
  const historicalData: DataPoint[] = [
    {
      month: 'June',
      revenue: 12247.50,
      orders: 73,
      avgTicket: 167.77,
      adSpend: 2590.00,
      roi: 4.73,
      cpa: 35.48,
      growth: 0
    },
    {
      month: 'July',
      revenue: 15867.51,
      orders: 91,
      avgTicket: 174.37,
      adSpend: 3770.00,
      roi: 4.21,
      cpa: 41.43,
      growth: 29.56
    },
    {
      month: 'August',
      revenue: 24361.35,
      orders: 133,
      avgTicket: 183.17,
      adSpend: 4270.00,
      roi: 5.71,
      cpa: 32.11,
      growth: 53.53
    },
    {
      month: 'September',
      revenue: 28947.27,
      orders: 159,
      avgTicket: 182.06,
      adSpend: 4800.00,
      roi: 6.03,
      cpa: 30.19,
      growth: 18.82
    }
  ];

  const generateProjections = (): DataPoint[] => {
    const projectedMonths: string[] = [
      'October', 'November', 'December', 'January', 'February', 'March',
      'April', 'May', 'June (Next)', 'July (Next)', 'August (Next)', 'September (Next)'
    ];

    const recentGrowthRates: number[] = [29.56, 53.53, 18.82];
    const avgGrowthRate: number = recentGrowthRates.reduce((a, b) => a + b, 0) / recentGrowthRates.length;
    const moderatedGrowthRate: number = Math.max(5, avgGrowthRate * 0.7);

    const lastData: DataPoint = historicalData[historicalData.length - 1];
    const projections: DataPoint[] = [];

    for (let i = 0; i < 12; i++) {
      const growthFactor: number = 1 + (moderatedGrowthRate - (i * 1.2)) / 100;
      const adjustedGrowthFactor: number = Math.max(1.02, growthFactor);
      
      const prevData: DataPoint = i === 0 ? lastData : projections[i - 1];
      
      const newRevenue: number = prevData.revenue * adjustedGrowthFactor;
      const newOrders: number = Math.round(prevData.orders * adjustedGrowthFactor);
      const newAdSpend: number = prevData.adSpend * (1 + 0.05);
      
      projections.push({
        month: projectedMonths[i],
        revenue: Math.round(newRevenue * 100) / 100,
        orders: newOrders,
        avgTicket: Math.round((newRevenue / newOrders) * 100) / 100,
        adSpend: Math.round(newAdSpend * 100) / 100,
        roi: Math.round((newRevenue / newAdSpend) * 100) / 100,
        cpa: Math.round((newAdSpend / newOrders) * 100) / 100,
        growth: Math.round(((newRevenue - prevData.revenue) / prevData.revenue) * 10000) / 100,
        isProjected: true
      });
    }

    return projections;
  };

  const allData: DataPoint[] = React.useMemo(() => {
    const projections: DataPoint[] = showProjections ? generateProjections() : [];
    return [...historicalData, ...projections];
  }, [showProjections]);

  const formatCurrency = (value: number): string => `$${value.toLocaleString()}`;

  const currentMetrics: DataPoint = historicalData[historicalData.length - 1];
  const previousMetrics: DataPoint = historicalData[historicalData.length - 2];

  const MetricCard: React.FC<MetricCardProps> = ({ 
    title, 
    current, 
    previous, 
    formatter = (x: number) => x.toString(), 
    isGood = (curr: number, prev: number) => curr > prev 
  }) => {
    const change: number = ((current - previous) / previous) * 100;
    const isPositive: boolean = isGood(current, previous);
    
    return (
      <div className="bg-white p-4 rounded-lg shadow-md border">
        <h3 className="text-sm font-medium text-gray-500 mb-1">{title}</h3>
        <div className="text-2xl font-bold text-gray-900 mb-1">
          {formatter(current)}
        </div>
        <div className={`text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
          {isPositive ? '↗' : '↘'} {Math.abs(change).toFixed(1)}% vs last month
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with Back Button */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="mb-4 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
          >
            ← Back to Home
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Mobility Scooter Rental - Performance Dashboard
          </h1>
          <div className="flex items-center gap-4">
            <p className="text-gray-600">Google Ads Campaign Analytics</p>
            <button
              onClick={() => setShowProjections(!showProjections)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                showProjections 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {showProjections ? 'Hide' : 'Show'} 12-Month Projections
            </button>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Revenue"
            current={currentMetrics.revenue}
            previous={previousMetrics.revenue}
            formatter={formatCurrency}
          />
          <MetricCard
            title="New Orders"
            current={currentMetrics.orders}
            previous={previousMetrics.orders}
          />
          <MetricCard
            title="ROI"
            current={currentMetrics.roi}
            previous={previousMetrics.roi}
            formatter={(x: number) => `${x}x`}
          />
          <MetricCard
            title="Cost per Acquisition"
            current={currentMetrics.cpa}
            previous={previousMetrics.cpa}
            formatter={formatCurrency}
            isGood={(curr: number, prev: number) => curr < prev}
          />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Revenue and Ad Spend Chart */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Revenue vs Ad Spend</h2>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={allData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis />
                <Tooltip formatter={(value: any, name: string) => [formatCurrency(Number(value)), name]} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="revenue" 
                  stackId="1"
                  stroke="#10b981" 
                  fill="#10b981"
                  fillOpacity={0.3}
                  name="Revenue"
                />
                <Area 
                  type="monotone" 
                  dataKey="adSpend" 
                  stackId="2"
                  stroke="#ef4444" 
                  fill="#ef4444"
                  fillOpacity={0.3}
                  name="Ad Spend"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* ROI Trend */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Return on Investment</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={allData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis />
                <Tooltip formatter={(value: any) => [`${value}x`, 'ROI']} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="roi" 
                  stroke="#8b5cf6" 
                  strokeWidth={3}
                  name="ROI (x)"
                  dot={{ fill: '#8b5cf6', r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Orders and Average Ticket */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Orders & Average Ticket</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={allData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar 
                  yAxisId="left"
                  dataKey="orders" 
                  fill="#3b82f6"
                  name="Orders"
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="avgTicket" 
                  stroke="#f59e0b" 
                  strokeWidth={3}
                  name="Avg Ticket ($)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Growth Rate */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Monthly Growth Rate</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={allData.filter((d: DataPoint) => d.growth !== 0)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis />
                <Tooltip formatter={(value: any) => [`${value}%`, 'Growth']} />
                <Legend />
                <Bar 
                  dataKey="growth" 
                  fill="#06b6d4"
                  name="Growth (%)"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Summary Stats */}
        {showProjections && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">12-Month Projection Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {formatCurrency(allData[allData.length - 1].revenue)}
                </div>
                <div className="text-gray-600">Projected Revenue (Month 12)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {allData[allData.length - 1].orders}
                </div>
                <div className="text-gray-600">Projected Orders (Month 12)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">
                  {allData[allData.length - 1].roi}x
                </div>
                <div className="text-gray-600">Projected ROI (Month 12)</div>
              </div>
            </div>
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Projections are based on historical trends with moderated growth assumptions. 
                Actual results may vary based on market conditions, seasonality, and campaign optimizations.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Campaign Cost Estimator Component
const CampaignCostEstimator: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [niches, setNiches] = useState<any[]>([]);
  const [selectedNiche, setSelectedNiche] = useState<string>('');
  const [nicheDetails, setNicheDetails] = useState<any>(null);
  const [customBudget, setCustomBudget] = useState<string>('');
  const [calculations, setCalculations] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [stats, setStats] = useState<any>(null);

  // Load niches on component mount
  React.useEffect(() => {
    loadNiches();
    loadStats();
  }, []);

  const loadNiches = async () => {
    try {
      const response = await fetch('/api/niches.php?action=list');
      const data = await response.json();
      if (data.success) {
        setNiches(data.data);
      }
    } catch (error) {
      console.error('Error loading niches:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch('/api/niches.php?action=stats');
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleNicheSelect = async (nicheId: string) => {
    if (!nicheId) {
      setSelectedNiche('');
      setNicheDetails(null);
      setCalculations(null);
      return;
    }

    setSelectedNiche(nicheId);
    setLoading(true);

    try {
      const response = await fetch(`/api/niches.php?action=details&id=${nicheId}`);
      const data = await response.json();
      if (data.success) {
        setNicheDetails(data.data);
        setCustomBudget(data.data.min_budget.toString());
      }
    } catch (error) {
      console.error('Error loading niche details:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateCustomBudget = async () => {
    if (!selectedNiche || !customBudget) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('niche_id', selectedNiche);
      formData.append('budget', customBudget);

      const response = await fetch('/api/niches.php?action=calculate', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        setCalculations(data.data);
      }
    } catch (error) {
      console.error('Error calculating costs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number): string => `${value.toLocaleString()}`;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <button
          onClick={onBack}
          className="mb-4 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
        >
          ← Back to Home
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Campaign Cost Estimator
          </h1>
          <p className="text-gray-600">
            Estimate Google Ads costs and expected leads for different market niches
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="text-sm font-medium text-gray-500 mb-1">Total Niches</h3>
              <div className="text-2xl font-bold text-blue-600">{stats.total_niches}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="text-sm font-medium text-gray-500 mb-1">Actual Data</h3>
              <div className="text-2xl font-bold text-green-600">{stats.actual_data}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="text-sm font-medium text-gray-500 mb-1">Estimated Data</h3>
              <div className="text-2xl font-bold text-orange-600">{stats.estimated_data}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="text-sm font-medium text-gray-500 mb-1">Avg Min Budget</h3>
              <div className="text-2xl font-bold text-purple-600">
                {formatCurrency(parseFloat(stats.avg_budget_overall))}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Niche Selection */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Select Niche & Budget</h2>
            
            {/* Niche Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Choose a Niche
              </label>
              <select
                value={selectedNiche}
                onChange={(e) => handleNicheSelect(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a niche...</option>
                {niches.map((niche) => (
                  <option key={niche.id} value={niche.id}>
                    {niche.niche_name} {niche.is_estimated ? '(Estimated)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Budget Input */}
            {nicheDetails && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Ad Budget
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-500">$</span>
                  <input
                    type="number"
                    value={customBudget}
                    onChange={(e) => setCustomBudget(e.target.value)}
                    className="w-full pl-8 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your budget"
                    min="0"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Minimum recommended: {formatCurrency(nicheDetails.min_budget)}
                </p>
              </div>
            )}

            {/* Calculate Button */}
            {nicheDetails && customBudget && (
              <button
                onClick={calculateCustomBudget}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Calculating...' : 'Calculate Results'}
              </button>
            )}
          </div>

          {/* Right Column - Results */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Cost Estimates</h2>
            
            {!selectedNiche && (
              <div className="text-center py-12 text-gray-500">
                <div className="text-4xl mb-4">📊</div>
                <p>Select a niche to see cost estimates</p>
              </div>
            )}

            {nicheDetails && !calculations && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">{nicheDetails.niche_name}</h3>
                  {nicheDetails.is_estimated && (
                    <div className="mb-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded inline-block">
                      Estimated Data
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-sm text-gray-600">Min Budget</p>
                      <p className="font-semibold">{formatCurrency(nicheDetails.min_budget)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Cost Per Lead</p>
                      <p className="font-semibold">
                        {formatCurrency(nicheDetails.cost_per_lead_min)} - {formatCurrency(nicheDetails.cost_per_lead_max)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Expected Leads</p>
                      <p className="font-semibold">
                        {Math.round(nicheDetails.leads_min)} - {Math.round(nicheDetails.leads_max)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Budget Range</p>
                      <p className="font-semibold">Monthly</p>
                    </div>
                  </div>

                  {nicheDetails.notes && (
                    <div className="mt-4 p-3 bg-blue-50 rounded">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> {nicheDetails.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {calculations && (
              <div className="space-y-6">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-green-900 mb-4">
                    Results for {formatCurrency(calculations.custom_budget)} Budget
                  </h3>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div className="bg-white p-4 rounded border">
                      <h4 className="font-medium text-gray-900 mb-2">Expected Leads per Month</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Minimum:</span>
                          <span className="font-semibold">{calculations.estimated_leads.min} leads</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Average:</span>
                          <span className="font-semibold text-blue-600">{calculations.estimated_leads.average} leads</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Maximum:</span>
                          <span className="font-semibold">{calculations.estimated_leads.max} leads</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded border">
                      <h4 className="font-medium text-gray-900 mb-2">Cost Per Lead Range</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Best Case:</span>
                          <span className="font-semibold text-green-600">
                            {formatCurrency(calculations.cost_per_lead.min)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Average:</span>
                          <span className="font-semibold text-blue-600">
                            {formatCurrency(calculations.cost_per_lead.average)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Worst Case:</span>
                          <span className="font-semibold text-red-600">
                            {formatCurrency(calculations.cost_per_lead.max)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {calculations.niche.is_estimated && (
                      <div className="bg-orange-50 p-3 rounded border border-orange-200">
                        <p className="text-sm text-orange-800">
                          ⚠️ <strong>Note:</strong> This data is estimated based on similar niches. 
                          Actual results may vary significantly.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App Component
const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<PageType>('home');

  const handleNavigate = (page: PageType) => {
    setCurrentPage(page);
  };

  const handleBack = () => {
    setCurrentPage('home');
  };

  return (
    <div className="App">
      {currentPage === 'home' && <HomePage onNavigate={handleNavigate} />}
      {currentPage === 'mobility' && <MobilityDashboard onBack={handleBack} />}
      {currentPage === 'campaign-cost' && <CampaignCostEstimator onBack={handleBack} />}
    </div>
  );
};

export default App;
